import React from "react";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { appConfig } from "../../config/app.config";

export function HomeFooter({ onDownload }: { onDownload: () => void }) {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const linkClass = "rounded-sm transition-colors hover:text-action-primary-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-primary-border";
  const scrollToSection = (event: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    event.preventDefault();
    const target = document.getElementById(sectionId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${sectionId}`);
      return;
    }
    navigate(`/#${sectionId}`);
  };

  return (
    <footer className="bg-[var(--home-ink)] text-white">
      <div className="mx-auto max-w-[1296px] px-5 py-9 sm:px-8 lg:px-0">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div className="max-w-xl">
            <button type="button" onClick={() => { navigate("/"); }} className="flex items-center gap-3" aria-label={appConfig.name}>
              <span className="flex h-[38px] w-[38px] items-center justify-center rounded-md bg-action-primary text-white">
                {React.createElement(appConfig.icon.component, { className: "h-5 w-5" })}
              </span>
              <span className="flex flex-col items-start">
                <span className="font-mono text-sm font-bold uppercase tracking-normal">{appConfig.name}</span>
                <span className="font-mono text-[10px] font-semibold uppercase tracking-normal opacity-70">DSH Desktop</span>
              </span>
            </button>
            <p className="mt-3 text-sm leading-6 text-[var(--home-footer-muted)]">{t("home.footerPromise")}</p>
          </div>
          <nav className="flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium" aria-label={t("home.footerNavigation")}>
            <a href="#services" onClick={(event) => scrollToSection(event, "services")} className={linkClass}>{t("home.navServices")}</a>
            <a href="#workflow" onClick={(event) => scrollToSection(event, "workflow")} className={linkClass}>{t("home.navWorkflow")}</a>
            <button type="button" onClick={() => { navigate("/login"); }} className={linkClass}>{t("home.signIn")}</button>
            <button type="button" onClick={onDownload} className={linkClass}>{t("home.navDownload")}</button>
          </nav>
        </div>
        <div className="mt-8 border-t border-white/10 pt-6">
          <div className="flex flex-col gap-4 text-xs md:flex-row md:items-center md:justify-between">
            <span className="font-mono text-[11px] text-[var(--home-footer-subtle)]">{t("home.footerCopyright")}</span>
            <div className="flex flex-wrap gap-5 text-[var(--home-footer-muted)]">
              <span>{t("home.footerPrivacy")}</span>
              <button type="button" onClick={() => { navigate("/terms"); }} className={linkClass}>{t("home.footerTerms")}</button>
              <button type="button" onClick={onDownload} className={`inline-flex items-center gap-1 ${linkClass}`}>
                {t("home.footerReleases")}<Download className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
